import React, { useEffect, useRef } from "react";
import { Box, Textarea } from "@chakra-ui/react";
import { useDraggable } from "../useDraggable";
import { c } from "../../classroomColors";

/** Free text on the board: no card, just the words, so it reads as a label
 *  rather than another note. */
const TextItem = ({ item, isSelected, readOnly, onChange, onMove, onSelect }) => {
  const ref = useRef(null);
  const { onPointerDown } = useDraggable({
    x: item.x, y: item.y, onMove, onStart: onSelect, disabled: readOnly,
  });

  useEffect(() => {
    if (!item.autoFocus || !ref.current) return undefined;
    // The click that created this item has not finished: the browser sets
    // focus itself on mousedown, after our handler, and would blur us again.
    // A macrotask lands after it. Deliberately not requestAnimationFrame,
    // which never fires while the tab is in the background — the caret would
    // silently not arrive.
    const t = setTimeout(() => ref.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [item.autoFocus]);

  return (
    <Box
      position="absolute"
      left={`${item.x}px`}
      top={`${item.y}px`}
      minW="150px"
      borderRadius="6px"
      // Selection is drawn by the frame around it.
      cursor={readOnly ? "default" : "grab"}
      _active={{ cursor: readOnly ? "default" : "grabbing" }}
      onPointerDown={onPointerDown}
    >
      <Textarea
        ref={ref}
        data-no-drag
        value={item.text || ""}
        onChange={(e) => onChange({ text: e.target.value })}
        onFocus={onSelect}
        isReadOnly={readOnly}
        placeholder="Type here"
        variant="unstyled"
        resize="none"
        rows={1}
        px="6px"
        py="3px"
        minH="30px"
        fontSize="17px"
        fontWeight="500"
        lineHeight="1.35"
        color={item.colour}
        _placeholder={{ color: c.textMuted }}
      />
    </Box>
  );
};

export default TextItem;
